import { useMutation, useQueryClient } from "@tanstack/react-query"
import { routes, type RelistRunPolicy } from "@nova-ai/shared"
import { z } from "zod"
import { apiFetch } from "../lib/api"
import { humanizeMarketplaceId } from "../lib/marketplace"
import { queryKeys } from "./query-keys"
import { relistRunInboxQueryOptions } from "./use-runs"

export function useSetRelistPolicy() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: { policy: RelistRunPolicy; enabled: boolean }) => {
      let saveError: string | null = null
      let saved: { enabled: boolean; revision: number } | null = null
      const name = humanizeMarketplaceId(input.policy.marketplace)
      await client.cancelQueries({ queryKey: queryKeys.relistRunInbox })
      try {
        const result = await apiFetch<unknown>(
          routes.listingAutomation.update({ marketplace: input.policy.marketplace }),
          {
            method: "PUT",
            body: { enabled: input.enabled, expectedRevision: input.policy.revision },
          }
        )
        if (result.ok) {
          saved = z
            .object({ enabled: z.boolean(), revision: z.number().int().nonnegative() })
            .parse(result.value)
        } else {
          saveError =
            result.error.kind === "conflict"
              ? `Someone else changed ${name} a moment ago. We reloaded the current setting. Try again if you still want to change it.`
              : `Save failed or its response was uncertain: ${result.error.message}. We reloaded the current setting.`
        }
      } catch {
        saveError = "The save response was uncertain. We reloaded the current setting."
      }
      // A focus-triggered read may have started during the write. Cancel it too.
      await client.cancelQueries({ queryKey: queryKeys.relistRunInbox })
      await client.invalidateQueries({ queryKey: queryKeys.relistRunInbox, refetchType: "none" })
      try {
        const inbox = await client.fetchQuery({
          ...relistRunInboxQueryOptions,
          staleTime: 0,
          retry: false,
        })
        if (saveError) {
          return { ok: false as const, message: saveError, requiresRefresh: false }
        }
        const current = inbox.marketplaces.find(
          (row) => row.marketplace === input.policy.marketplace
        )
        if (!saved || current?.revision !== saved.revision || current.enabled !== input.enabled) {
          return {
            ok: false as const,
            message: `The setting for ${name} changed during confirmation. We reloaded the current setting.`,
            requiresRefresh: false,
          }
        }
        return { ok: true as const, policy: current }
      } catch {
        return {
          ok: false as const,
          message: "The saved state could not be confirmed. Refresh before making another change.",
          requiresRefresh: true,
        }
      }
    },
  })
}
